import { FC, useMemo } from 'react';
import {
  Timeline,
  Text,
  Group,
  Stack,
  ThemeIcon,
  Badge,
  Center,
  Loader,
  ScrollArea,
  Paper,
  Box,
  Divider,
} from '@mantine/core';
import {
  IconStethoscope,
  IconClipboardList,
  IconCash,
  IconCalendarEvent,
  IconUser,
  IconCheck,
  IconX,
  IconMinus,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  Consent,
  Encounter,
  Invoice,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { formatMoney } from '@medplum/core';
import { formatDate } from '../lims/utils/common-helpers';
import { PaymentStatusBadge } from '../lims/components/PaymentStatusBadge';
import { PaymentStatus } from '../lims/lims-types';
import { OrderUtils } from '../lims/utils/order-table.utils';
import { getLinkedOrderRef } from '../lims/utils/payment-helpers';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';
import { ConsentStatus } from '@/types/consent.types';

// ---------------------------------------------------------------------------

type OrderItem = {
  order: ServiceRequest;
  invoice: Invoice | undefined;
  consent: Consent | undefined;
};

type TimelineEntry =
  | { kind: 'encounter'; date: string; resource: Encounter }
  | { kind: 'orders'; date: string; dayKey: string; items: OrderItem[] };

interface PatientTimelineProps {
  encounters: Encounter[];
  orders: ServiceRequest[];
  invoices: Invoice[];
  consents?: Map<string, Consent>;
  loading: boolean;
  onEncounterClick?: (encounter: Encounter) => void;
  onOrderClick?: (order: ServiceRequest) => void;
  onInvoiceClick?: (invoice: Invoice) => void;
}

// ---------------------------------------------------------------------------

export const PatientTimeline: FC<PatientTimelineProps> = ({
  encounters,
  orders,
  invoices,
  consents = new Map(),
  loading,
  onEncounterClick,
  onOrderClick,
  onInvoiceClick,
}) => {
  const { t } = useTranslation(['lims', 'encounters']);

  const entries = useMemo<TimelineEntry[]>(() => {
    // Invoice lookup: orderId -> Invoice
    const invoiceByOrderId = new Map<string, Invoice>();
    for (const inv of invoices) {
      const ref = getLinkedOrderRef(inv);
      if (ref) {
        invoiceByOrderId.set(ref.replace('ServiceRequest/', ''), inv);
      }
    }

    // Group orders by calendar day (YYYY-MM-DD)
    const ordersByDay = new Map<string, OrderItem[]>();
    for (const order of orders) {
      const rawDate = order.authoredOn || order.meta?.lastUpdated || '';
      const dayKey = rawDate ? rawDate.slice(0, 10) : 'unknown';
      const bucket = ordersByDay.get(dayKey) ?? [];
      bucket.push({
        order,
        invoice: invoiceByOrderId.get(order.id ?? ''),
        consent: consents.get(order.id ?? ''),
      });
      ordersByDay.set(dayKey, bucket);
    }

    const encounterEntries: TimelineEntry[] = encounters.map((enc) => ({
      kind: 'encounter',
      date: enc.period?.start || enc.meta?.lastUpdated || '',
      resource: enc,
    }));

    const orderEntries: TimelineEntry[] = Array.from(ordersByDay.entries()).map(
      ([dayKey, items]) => ({
        kind: 'orders',
        date:
          items[0].order.authoredOn || items[0].order.meta?.lastUpdated || '',
        dayKey,
        items,
      })
    );

    return [...encounterEntries, ...orderEntries].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [encounters, orders, invoices, consents]);

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  if (entries.length === 0) {
    return (
      <Center p="xl">
        <Text c="dimmed" size="sm">
          {t(
            'secretaryPaymentsPage.noTimeline',
            'No events found for this patient',
            { ns: 'lims' }
          )}
        </Text>
      </Center>
    );
  }

  return (
    <ScrollArea.Autosize mah={600} offsetScrollbars px="md">
      <Timeline
        active={entries.length - 1}
        bulletSize={30}
        lineWidth={2}
        mt="md"
      >
        {entries.map((entry, idx) =>
          entry.kind === 'encounter' ? (
            <Timeline.Item
              key={`enc-${entry.resource.id ?? idx}`}
              bullet={
                <ThemeIcon size={30} radius="xl" color="blue" variant="filled">
                  <IconStethoscope size={16} />
                </ThemeIcon>
              }
              title={<DateLabel date={entry.date} />}
            >
              <Box mt={6} mb="sm">
                <EncounterCard
                  encounter={entry.resource}
                  onClick={onEncounterClick}
                  t={t}
                />
              </Box>
            </Timeline.Item>
          ) : (
            <Timeline.Item
              key={`orders-${entry.dayKey}`}
              bullet={
                <ThemeIcon
                  size={30}
                  radius="xl"
                  color="violet"
                  variant="filled"
                >
                  <IconClipboardList size={16} />
                </ThemeIcon>
              }
              title={<DateLabel date={entry.date} />}
            >
              <Stack gap={8} mt={6} mb="sm">
                {entry.items.map(({ order, invoice, consent }) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    invoice={invoice}
                    consent={consent}
                    onOrderClick={onOrderClick}
                    onInvoiceClick={onInvoiceClick}
                    t={t}
                  />
                ))}
              </Stack>
            </Timeline.Item>
          )
        )}
      </Timeline>
    </ScrollArea.Autosize>
  );
};

// ---------------------------------------------------------------------------
// DateLabel — shown as the Timeline.Item title for visual chronology

const DateLabel: FC<{ date: string }> = ({ date }) => (
  <Group gap={4} align="center">
    <Box component={IconCalendarEvent} size={12} c="dimmed" />
    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
      {date ? formatDate(date) : '—'}
    </Text>
  </Group>
);

// ---------------------------------------------------------------------------
// EncounterCard

interface EncounterCardProps {
  encounter: Encounter;
  onClick?: (enc: Encounter) => void;
  t: any;
}

const EncounterCard: FC<EncounterCardProps> = ({ encounter, onClick, t }) => {
  const typeDisplay =
    encounter.type?.[0]?.coding?.[0]?.display || encounter.type?.[0]?.text;

  return (
    <Paper withBorder radius="sm" p="xs" onClick={() => onClick?.(encounter)}>
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size={22} radius="xl" color="blue" variant="light">
          <IconStethoscope size={12} />
        </ThemeIcon>
        <Stack gap={1} flex={1}>
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" fw={600}>
              {t('secretaryPaymentsPage.timeline.encounter', 'Encounter', {
                ns: 'lims',
              })}
            </Text>
            <Badge size="xs" color="blue" variant="light">
              {t(`statusOptions.${encounter.status}`, {
                ns: 'encounters',
                defaultValue: encounter.status,
              })}
            </Badge>
          </Group>
          {typeDisplay && (
            <Text size="xs" c="dimmed">
              {typeDisplay}
            </Text>
          )}
        </Stack>
      </Group>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// OrderCard

interface OrderCardProps {
  order: ServiceRequest;
  invoice: Invoice | undefined;
  consent: Consent | undefined;
  onOrderClick?: (order: ServiceRequest) => void;
  onInvoiceClick?: (invoice: Invoice) => void;
  t: any;
}

const OrderCard: FC<OrderCardProps> = ({
  order,
  invoice,
  consent,
  onOrderClick,
  onInvoiceClick,
  t,
}) => {
  const testName = OrderUtils.getTestName(
    order,
    t('orderList.unknownTest', 'Unknown Test', { ns: 'lims' })
  );
  const orderId = OrderUtils.getOrderId(order);
  const subStateStatus = OrderUtils.getSubStateStatus(order);
  const subStateName = OrderUtils.getSubStateName(order);
  const assignedPerson =
    order.requester?.display || order.performer?.[0]?.display;
  const specimenCount = order.specimen?.length ?? 0;
  const consentStatus: ConsentStatus =
    (consent?.provision?.type as ConsentStatus) ?? 'not-answered';
  return (
    <Paper withBorder radius="sm" p="sm">
      <Stack gap={8}>
        {/* Two-column layout: info left, statuses right */}
        <Group
          gap="sm"
          wrap="nowrap"
          align="flex-start"
          onClick={() => onOrderClick?.(order)}
        >
          {/* Left: icon + name + id + person */}
          <ThemeIcon size={28} radius="xl" color="violet" variant="light">
            <IconClipboardList size={15} />
          </ThemeIcon>
          <Stack gap={3} flex={1}>
            <Text size="sm" fw={700} lh={1.3}>
              {testName}
            </Text>
            {orderId && (
              <Text size="xs" c="dimmed">
                {orderId}
              </Text>
            )}
            {assignedPerson && (
              <Group gap={4} wrap="nowrap">
                <Box component={IconUser} size={11} c="dimmed" />
                <Text size="xs" c="dimmed">
                  {assignedPerson}
                </Text>
              </Group>
            )}
          </Stack>

          {/* Right: all statuses stacked */}
          <Stack gap={6} align="flex-end">
            <StatusBadge
              status={subStateStatus}
              label={t(subStateName, { ns: 'lims' })}
              size="sm"
              variant="light"
            />
            {order.priority && order.priority !== 'routine' && (
              <StatusBadge status={order.priority} size="sm" variant="light" />
            )}
            <Group gap="sm" wrap="nowrap">
              <Group gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  {t('secretaryPaymentsPage.timeline.specimen', 'Specimen', {
                    ns: 'lims',
                  })}
                </Text>
                {specimenCount > 0 ? (
                  <IconCheck size={13} color="var(--mantine-color-teal-6)" />
                ) : (
                  <IconMinus size={13} color="var(--mantine-color-gray-5)" />
                )}
              </Group>
              <Group gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  {t('secretaryPaymentsPage.timeline.consent', 'Consent', {
                    ns: 'lims',
                  })}
                </Text>
                {consentStatus === 'permit' ? (
                  <IconCheck size={13} color="var(--mantine-color-green-6)" />
                ) : consentStatus === 'deny' ? (
                  <IconX size={13} color="var(--mantine-color-red-6)" />
                ) : (
                  <IconMinus size={13} color="var(--mantine-color-gray-5)" />
                )}
              </Group>
            </Group>
          </Stack>
        </Group>

        {/* Payment row */}
        <Divider />
        {invoice ? (
          <Group
            gap="xs"
            wrap="nowrap"
            onClick={() => onInvoiceClick?.(invoice)}
          >
            <ThemeIcon size={22} radius="xl" color="green" variant="light">
              <IconCash size={12} />
            </ThemeIcon>
            <PaymentStatusBadge
              status={(invoice.status as PaymentStatus) || 'draft'}
              size="xs"
            />
            {invoice.totalNet && (
              <Text size="xs" fw={600} flex={1}>
                {formatMoney(invoice.totalNet)}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {formatDate(invoice.date || invoice.meta?.lastUpdated || '')}
            </Text>
          </Group>
        ) : (
          <Group gap="xs">
            <ThemeIcon size={22} radius="xl" color="gray" variant="light">
              <IconCash size={12} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">
              {t('secretaryPaymentsPage.timeline.noPayment', 'No payment yet', {
                ns: 'lims',
              })}
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};
